import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import MinimizableComposeWindow from '../components/email/MinimizableComposeWindow';
import { useNavigate, useLocation } from 'react-router-dom';

// Copiado de EmailLayout.tsx
export interface ComposeSession {
  id: string;
  context: any;
}

const ComposeModalsContext = createContext<any>(null);

export const ComposeModalsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [composeSessions, setComposeSessions] = useState<ComposeSession[]>([]);
  const [windowStates, setWindowStates] = useState<Record<string, { minimized: boolean; fullScreen: boolean }>>({});
  const [aiGeneratedContent, setAiGeneratedContent] = useState<Record<string, string>>({});
  const [currentSelectedPaciente, setCurrentSelectedPaciente] = useState<any | null>(null);
  const [emailsLoaded, setEmailsLoaded] = useState(false);
  const [switchingPatientModalId, setSwitchingPatientModalId] = useState<string | null>(null);
  const previousPacienteRef = useRef<any | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Handlers
  const openComposeModal = useCallback((context: any) => {
    if (composeSessions.length >= 3) {
      return null; // No abrir más de 3
    }
    const id = Math.random().toString(36).substr(2, 9);
    setComposeSessions(prev => [...prev, { id, context }]);
    return id;
  }, [composeSessions]);

  const closeComposeModal = useCallback((id: string) => {
    setComposeSessions(prev => prev.filter(s => s.id !== id));
    setWindowStates(prev => { const copy = { ...prev }; delete copy[id]; return copy; });
    setAiGeneratedContent(prev => { const copy = { ...prev }; delete copy[id]; return copy; });
  }, []);

  const minimizeComposeModal = useCallback((id: string) => {
    setWindowStates(prev => ({ ...prev, [id]: { ...prev[id], minimized: true } }));
  }, []);

  const maximizeComposeModal = useCallback((id: string) => {
    setWindowStates(prev => ({ ...prev, [id]: { ...prev[id], fullScreen: true } }));
  }, []);

  const restoreComposeModal = useCallback((id: string) => {
    setWindowStates(prev => ({ ...prev, [id]: { minimized: false, fullScreen: false } }));
  }, []);

  const updateComposeModal = useCallback((id: string, context: any) => {
    setComposeSessions(prev => prev.map(s => s.id === id ? { ...s, context: { ...s.context, ...context } } : s));
  }, []);

  const setAIGeneratedContentForModal = useCallback((id: string, content: string) => {
    setAiGeneratedContent(prev => ({ ...prev, [id]: content }));
  }, []);

  const clearAIGeneratedContentForModal = useCallback((id: string) => {
    setAiGeneratedContent(prev => { const copy = { ...prev }; delete copy[id]; return copy; });
  }, []);

  // Navegar a emails y cargar paciente desde cualquier lugar
  const goToPacienteEmails = useCallback((pacienteId: string) => {
    // console.log('[DEBUG] ComposeModalsContext - Navegando a pantalla de emails');
    navigate('/email');
  }, [navigate]);

  // Handler para cuando se envía un email exitosamente
  const handleSendSuccess = useCallback(() => {
    // Aquí podrías agregar lógica para refrescar emails o mostrar notificación
    console.log('Email enviado exitosamente');
  }, []);

  // Render global de modals
  // Función para calcular el ancho real de una ventana según su estado
  const getWindowWidth = (session: ComposeSession, isMinimized: boolean, isFullScreen: boolean) => {
    if (isFullScreen) return window.innerWidth * 0.4 - 32; // 40% de la pantalla
    if (isMinimized) {
      // Ancho fijo más compacto para todas las ventanas minimizadas
      return 220; // Ancho más compacto que mantiene el espaciado original
    }
    return 560; // Ancho normal
  };

  // Función para calcular la posición de cada ventana
  const calculateWindowPosition = (index: number) => {
    if (index < 0 || index >= composeSessions.length) return 16;
    
    let currentRight = 16; // Margen inicial desde la derecha
    
    // Calcular la posición sumando el ancho de todas las ventanas anteriores
    for (let i = 0; i < index; i++) {
      const session = composeSessions[i];
      if (!session) continue;
      
      // Obtener el estado actual de la ventana
      const windowState = windowStates[session.id] || { minimized: false, fullScreen: false };
      
      // Si la ventana está en pantalla completa, no afecta el posicionamiento de las otras
      if (windowState.fullScreen) continue;
      
      const width = getWindowWidth(session, windowState.minimized, windowState.fullScreen);
      currentRight += width + 30; // Ancho + gap de 30px
    }
    
    return currentRight;
  };

  // Actualizar paciente seleccionado globalmente
  const updateCurrentPaciente = useCallback((paciente: any | null) => {
    // console.log('[DEBUG] ComposeModalsContext - Actualizando paciente global:', {
    //   pacienteId: paciente?.id,
    //   pacienteName: paciente?.nombre,
    //   isNull: paciente === null
    // });
    setCurrentSelectedPaciente(paciente);
  }, []);

  // Función para cambiar paciente desde modals globales
  const handlePacienteChange = useCallback((paciente: any, initiatorModalId?: string) => {
    // console.log('[DEBUG] ComposeModalsContext - Cambiando paciente desde modal global:', {
    //   pacienteId: paciente?.id,
    //   pacienteName: paciente?.nombre,
    //   isNull: paciente === null || paciente === undefined
    // });
    
    // Guardar el paciente anterior antes de cambiarlo
    previousPacienteRef.current = currentSelectedPaciente;
    
    // Si un modal inició el cambio, lo registramos.
    if (initiatorModalId) {
        setSwitchingPatientModalId(initiatorModalId);
    }
    
    // Siempre limpiar emailsLoaded cuando se cambia de paciente desde modal compose
    // para forzar que aparezca el modal de selección de días
    setEmailsLoaded(false);
    
    setCurrentSelectedPaciente(paciente);
    
    // Si se deselecciona el paciente, también limpiar el estado de emails cargados
    if (!paciente) {
      setEmailsLoaded(false);
      // Si se deselecciona el paciente, limpiamos el ID del modal iniciador.
      setSwitchingPatientModalId(null);
    }
  }, [currentSelectedPaciente]);

  // Limpiar paciente seleccionado cuando se sale de la pantalla de emails (en el contexto global)
  React.useEffect(() => {
    // console.log('[DEBUG] ComposeModalsContext - Verificando si salir de pantalla emails:', {
    //   currentPath: location.pathname,
    //   isEmailPage: location.pathname === '/email'
    // });
    if (location.pathname !== '/email') {
      // console.log('[DEBUG] ComposeModalsContext - Limpiando paciente del contexto global');
      setCurrentSelectedPaciente(null);
    }
  }, [location.pathname]);

  // Función para actualizar estado de emails cargados
  const updateEmailsLoaded = useCallback((loaded: boolean) => {
    // console.log('[DEBUG] ComposeModalsContext - Actualizando estado de emails cargados:', loaded);
    setEmailsLoaded(loaded);
    // Cuando los emails terminan de cargar, limpiamos el ID del modal.
    // Esto apagará el spinner en el modal correspondiente.
    if (loaded) {
        setSwitchingPatientModalId(null);
        previousPacienteRef.current = null;
    }
  }, []);

  // ¡CORRECCIÓN! Definimos la función de cancelación con useCallback
  const clearSwitchingPatientModal = useCallback(() => {
    if (previousPacienteRef.current) {
        setCurrentSelectedPaciente(previousPacienteRef.current);
        setEmailsLoaded(true);
    }
    setSwitchingPatientModalId(null);
    previousPacienteRef.current = null;
  }, []); // Dependencia vacía es correcta, solo usa refs y setters.

  return (
    <ComposeModalsContext.Provider value={{
      composeSessions,
      windowStates,
      aiGeneratedContent,
      currentSelectedPaciente,
      emailsLoaded,
      switchingPatientModalId,
      openComposeModal,
      closeComposeModal,
      minimizeComposeModal,
      maximizeComposeModal,
      restoreComposeModal,
      updateComposeModal,
      setAIGeneratedContentForModal,
      clearAIGeneratedContentForModal,
      goToPacienteEmails,
      updateCurrentPaciente,
      updateEmailsLoaded,
      onPacienteChange: handlePacienteChange,
      clearSwitchingPatientModal
    }}>
      {children}
      {/* Render global de modals */}
      {(() => {
        const ANCHO_MINIMIZADO = 240;
        const ANCHO_MAXIMIZADO = 600;
        const ESPACIADO = 10;
        let cumulativeOffset = 16;
        return composeSessions.map((session, index) => {
          const windowState = windowStates[session.id] || { minimized: false, fullScreen: false };
          const currentOffset = cumulativeOffset;
          const currentWindowWidth = windowState.minimized ? ANCHO_MINIMIZADO : ANCHO_MAXIMIZADO;
          cumulativeOffset += currentWindowWidth + ESPACIADO;
          return (
            <MinimizableComposeWindow
              key={session.id}
              composeId={session.id}
              initialContext={session.context}
              zIndex={1300 + (composeSessions.length - 1 - index)} // Base más alto para estar por encima del AppBar
              rightOffset={currentOffset}
              windowState={windowState}
              aiGeneratedContent={aiGeneratedContent[session.id]}
              onAIGenerateComplete={content => setAIGeneratedContentForModal(session.id, content)}
              onAIPasteComplete={() => clearAIGeneratedContentForModal(session.id)}
              onClose={() => closeComposeModal(session.id)}
              onWindowStateChange={(id, state) => setWindowStates(prev => ({ ...prev, [id]: state }))}
              onGoToPacienteEmails={goToPacienteEmails}
              onSendSuccess={handleSendSuccess}
              currentSelectedPaciente={currentSelectedPaciente}
              onPacienteChange={(paciente) => handlePacienteChange(paciente, session.id)}
              emailsLoaded={emailsLoaded}
              isSwitchingPatient={switchingPatientModalId === session.id}
            />
          );
        });
      })()}
    </ComposeModalsContext.Provider>
  );
};

export const useComposeModals = () => useContext(ComposeModalsContext); 